import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Seq } from 'immutable';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { UnitPositionType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { UnitSummaryEntityFetchService } from 'src/store/generated/client-sync.generated';
import { UnitShiftSummaryProps } from 'src/store/generated/unit-shift-summary-record.generated';
import {
  selectUnitSummaryEntitiesByVehicleId,
  UnitSummaryProps,
  UnitSummaryViewModelBase,
} from 'src/store/generated/unit-summary-record.generated';
import { withTimezone } from 'src/util/date';
import { getMinutesOfDay } from 'src/util/helpers/date-helper';
import { unitNumberStr } from 'src/util/helpers/unit-helper';
import { undefinedOrMap } from 'src/util/rxjs/operators';
import { humanize } from 'src/util/text';

function toBaseTime(dt: Date, tzCode: string | null) {
  return tzCode == null ? dt : withTimezone(dt, tzCode);
}

function isCurrentShift(todayMinutes: number, shift: UnitShiftSummaryProps) {
  return shift.startTimeInMinutes < shift.endTimeInMinutes
    ? todayMinutes >= shift.startTimeInMinutes && todayMinutes <= shift.endTimeInMinutes
    : todayMinutes >= shift.startTimeInMinutes || todayMinutes <= shift.endTimeInMinutes;
}

type MissingPositionCount = number;
export type MissingResources =
  | false
  | ({ vehicle?: true; summary: string } & {
      [missingCrew in UnitPositionType]: MissingPositionCount;
    });
type UnitMissionProps = Pick<UnitSummaryProps, 'vehicleId' | 'positions'>;
export function getMissingResources(
  unitSummary: UnitMissionProps,
  isGroundUnit: boolean = false,
): MissingResources {
  let missingVehicle = false;
  if (unitSummary.vehicleId == null) {
    missingVehicle = true;
  }

  const missingFlightCrew: [type: UnitPositionType, count: number][] = unitSummary.positions
    .filter((p) => p.isFlightCrew && !p.isNonEmployee && p.teamMemberId == null)
    .groupBy((v) => v.type)
    .mapEntries(([type, missing]) => [type, missing.count()])
    .toArray();

  if (
    !isGroundUnit &&
    unitSummary.positions.find(
      (p) => p.type === UnitPositionType.Pilot && p.teamMemberId != null && p.isFlightCrew === true,
    ) == null &&
    missingFlightCrew.find(([type]) => type === UnitPositionType.Pilot) == null
  ) {
    missingFlightCrew.push([UnitPositionType.Pilot, 1]);
  }
  if (!missingVehicle && missingFlightCrew.length === 0) {
    return false;
  }

  const missingResources = missingFlightCrew.map(([type, count], i) => {
    const endingAnd = i === missingFlightCrew.length - 1 && i !== 0 ? 'and ' : '';
    return `${endingAnd}${count} ${humanize(type).toLowerCase()}(s)`;
  });
  if (missingVehicle) {
    missingResources.unshift('a vehicle');
  }

  const summary = `Missing ${missingResources.join(', ')}.`;

  return {
    vehicle: missingVehicle ? true : undefined,
    summary,
    ...Object.fromEntries(missingFlightCrew),
  } as MissingResources;
}

export class UnitSummaryViewModel extends UnitSummaryViewModelBase {
  constructor(props: UnitSummaryProps) {
    super(props);
  }

  public missingResources(isGround: boolean): MissingResources {
    return getMissingResources(this, isGround);
  }

  get unitNumberStr() {
    return unitNumberStr(this);
  }

  get isTeamMemberNonCompliant() {
    return this.positions.find((m) => m.isNonCompliant) != null;
  }

  get baseTimeInMinutes() {
    return getMinutesOfDay(toBaseTime(new Date(), this.baseTimezoneCd));
  }

  public getCurrentShift() {
    return this.shifts.find((shift) => isCurrentShift(this.baseTimeInMinutes, shift));
  }

  get oosReasons() {
    if (this.isOutOfService !== true) {
      return null;
    }

    const oosEvents = this.outOfServiceEvents;
    return oosEvents.map((o) => `${o.reasonName} (${o.type})`).join(', ');
  }

  get availableTime() {
    return this.plannedAvailableTime;
  }

  get availableNotes() {
    return this.plannedAvailableNotes;
  }
}

@Injectable({ providedIn: 'root' })
export class UnitSummaryViewModelService {
  constructor(
    private readonly _unitSummaryFetchService: UnitSummaryEntityFetchService,
    private _store: Store<AppState>,
  ) {}

  public getById(id: string): Observable<UnitSummaryViewModel | undefined> {
    return this._unitSummaryFetchService
      .getById(id)
      .pipe(undefinedOrMap((unitSummary) => new UnitSummaryViewModel(unitSummary)));
  }

  public getByIds(ids: string[]): Observable<Seq.Indexed<UnitSummaryViewModel> | undefined> {
    return this._unitSummaryFetchService
      .getByIds(ids)
      .pipe(
        undefinedOrMap((unitSummaries) =>
          unitSummaries.map((unitSummary) => new UnitSummaryViewModel(unitSummary)),
        ),
      );
  }

  public getByVehicleId(vehicleId: string): Observable<UnitSummaryViewModel | undefined> {
    return this._store.select(selectUnitSummaryEntitiesByVehicleId(vehicleId)).pipe(
      map((units) => {
        const unit = units.first(undefined);
        return unit != null ? new UnitSummaryViewModel(unit) : undefined;
      }),
    );
  }
}
